import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../../../hooks/useToast';
import { createPartnerAutomationFromDefault } from '../template-api';
import { templateKeys } from '../../template-query-keys';

export function useCreatePartnerAutomationFromDefault() {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (params: {
      defaultTemplateName: string;
      customName: string;
      customDescription: string;
    }) => createPartnerAutomationFromDefault(params),
    onMutate: params => {
      // Validate inputs before mutation
      if (!params.customName.trim() || !params.customDescription.trim() || !params.defaultTemplateName) {
        const validationError = new Error('Template name and description required');
        addToast({
          title: 'Template name and description required',
          description: 'Please enter a template name and description.',
          variant: 'destructive',
        });
        throw validationError;
      }
    },
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: templateKeys.lists() });

      // Validate result and show appropriate toast
      if (!result.created || !result.templateId) {
        addToast({
          title: 'Error creating template',
          description: 'The base template may not have been found.',
          variant: 'destructive',
        });
        return;
      }

      // Show success toast
      addToast({
        title: 'Template created successfully',
        description: `Template "${variables.customName}" has been created.`,
        variant: 'default',
      });
    },
    onError: (error: Error) => {
      // Only show error toast if it's not a validation error (validation error already shows toast in onMutate)
      if (error.message !== 'Template name and description required') {
        addToast({
          title: 'Error creating template',
          description: error instanceof Error ? error.message : 'Please try again.',
          variant: 'destructive',
        });
      }
    },
  });
}
